import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { useAuth } from '../../shared/auth/AuthContext';
import { formatDateTime, EMPTY } from '../../shared/utils/format';
import { Badge, Button, LoadingSkeleton, Modal, Pagination, Select } from '@nexora/design-system';
import './obligations.css';

/* ───────────────────────────────────────────────────────────────────────────
   Repositório de Arquivos Transmitidos — /obligations/transmissions.

   Consome:
   * GET /obligations/transmissions → PaginatedTransmissionsResponseDto
     (data: ArquivoTransmitidoDto[] com tipo, periodoApuracao,
     dataTransmissao, status, protocoloSefaz, recibo; filtros
     status/tipo/periodoApuracao/page/limit)

   Não há ação de escrita aqui: o repositório é alimentado pela transmissão
   do assistente SPED. O detalhe abre o recibo (JSON) devolvido pela SEFAZ.
   ─────────────────────────────────────────────────────────────────────────── */

type ArquivoStatus = 'GERADO' | 'VALIDADO' | 'ASSINADO' | 'TRANSMITIDO' | 'RECIBO_RECEBIDO' | 'REJEITADO';

interface ArquivoTransmitido {
  id: string;
  arquivoNome: string;
  tipo: string;
  periodoApuracao: string;
  dataTransmissao: string;
  status: ArquivoStatus;
  protocoloSefaz: string | null;
  recibo: Record<string, unknown> | null;
}

interface PaginatedTransmissions {
  data: ArquivoTransmitido[];
  total: number;
  totalPages: number;
}

const STATUS_LABEL: Record<string, string> = {
  GERADO: 'Gerado',
  VALIDADO: 'Validado',
  ASSINADO: 'Assinado',
  TRANSMITIDO: 'Transmitido',
  RECIBO_RECEBIDO: 'Recibo recebido',
  REJEITADO: 'Rejeitado',
};

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'error' | 'neutral' | 'info'> = {
  GERADO: 'neutral',
  VALIDADO: 'info',
  ASSINADO: 'info',
  TRANSMITIDO: 'info',
  RECIBO_RECEBIDO: 'success',
  REJEITADO: 'error',
};

export function TransmissionsPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';

  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(1);
  const [reciboOpen, setReciboOpen] = useState<ArquivoTransmitido | null>(null);
  const pageSize = 20;

  const listQuery = useQuery({
    queryKey: ['obligations', tenantId, 'transmissions', statusFilter, page],
    queryFn: () =>
      apiClient.get<PaginatedTransmissions>('/obligations/transmissions', {
        params: {
          page: String(page),
          limit: String(pageSize),
          ...(statusFilter !== 'ALL' ? { status: statusFilter } : {}),
        },
      }),
    enabled: !!tenantId,
  });

  const { data, isLoading, error } = listQuery;
  const arquivos = data?.data ?? [];
  const totalPages = Math.max(1, data?.totalPages ?? 1);

  return (
    <div className="obl-page">
      <div>
        <h1 className="font-display-lg text-display-lg text-on-surface m-0">Repositório de Arquivos</h1>
        <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
          Arquivos SPED transmitidos à SEFAZ, protocolo e recibo.
        </p>
      </div>

      <div className="obl-toolbar">
        <Select
          label="Status"
          selectSize="sm"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="ALL">Todos</option>
          {Object.entries(STATUS_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <span className="obl-count">{data?.total ?? 0} arquivos</span>
      </div>

      <div className="obl-card">
        <div className="obl-card__head">
          <h2 className="obl-card__title">Arquivos transmitidos</h2>
        </div>

        {isLoading && (
          <div style={{ padding: '1rem' }}>
            <LoadingSkeleton count={6} height="40px" />
          </div>
        )}

        {error && !isLoading && (
          <div className="obl-state">
            <p className="obl-state__title">Erro ao carregar transmissões</p>
            <p className="obl-state__detail">{(error as Error).message}</p>
            <Button variant="outline" size="sm" onClick={() => void listQuery.refetch()}>
              Tentar novamente
            </Button>
          </div>
        )}

        {!isLoading && !error && arquivos.length === 0 && (
          <div className="obl-state">
            <span className="material-symbols-outlined obl-state__icon" aria-hidden="true">
              folder_open
            </span>
            <p className="obl-state__detail">Nenhum arquivo transmitido ainda.</p>
          </div>
        )}

        {!isLoading && !error && arquivos.length > 0 && (
          <>
            <div className="obl-table">
              <table>
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Período</th>
                    <th>Data Transmissão</th>
                    <th>Status</th>
                    <th>Protocolo SEFAZ</th>
                    <th style={{ textAlign: 'right' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {arquivos.map((arq) => (
                    <tr key={arq.id}>
                      <td>
                        {arq.tipo}
                        <span className="obl-sub">{arq.arquivoNome}</span>
                      </td>
                      <td className="obl-mono">{arq.periodoApuracao.replace('-', '/')}</td>
                      <td className="obl-nowrap">{formatDateTime(arq.dataTransmissao)}</td>
                      <td>
                        <Badge variant={STATUS_VARIANT[arq.status] ?? 'neutral'} size="sm">
                          {STATUS_LABEL[arq.status] ?? arq.status}
                        </Badge>
                      </td>
                      <td className="obl-mono" style={{ wordBreak: 'break-all' }}>
                        {arq.protocoloSefaz ?? EMPTY}
                      </td>
                      <td className="obl-actions">
                        <Button variant="ghost" size="sm" onClick={() => setReciboOpen(arq)}>
                          Recibo
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="obl-footer">
              <span className="obl-count">
                Página {page} de {totalPages}
              </span>
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={!!reciboOpen}
        onClose={() => setReciboOpen(null)}
        title={`Recibo · ${reciboOpen?.arquivoNome ?? ''}`}
        size="lg"
        footer={
          <Button variant="outline" onClick={() => setReciboOpen(null)}>
            Fechar
          </Button>
        }
      >
        {reciboOpen?.recibo ? (
          <pre className="obl-preview">{JSON.stringify(reciboOpen.recibo, null, 2)}</pre>
        ) : (
          <p style={{ fontSize: '0.875rem', color: '#43474e' }}>
            Nenhum recibo registrado para este arquivo.
          </p>
        )}
      </Modal>
    </div>
  );
}